import type { HTMLAttributes, ReactNode, ThHTMLAttributes } from 'react'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { cn } from '../lib/cn.js'

/** الجداول قد تتجاوز عرض الشاشة — تمريرها أفقياً هنا لا على الصفحة */
export function TableWrapper({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('w-full overflow-x-auto rounded-xl border border-border bg-surface', className)} {...props} />
  )
}

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return <table className={cn('w-full border-collapse text-sm', className)} {...props} />
}

export function Thead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('bg-bg-subtle', className)} {...props} />
}

export function Tbody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('divide-y divide-border', className)} {...props} />
}

export function Tr({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('transition-colors hover:bg-surface-hover', className)} {...props} />
}

export interface ThProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'align'> {
  sortable?: boolean
  sortDir?: 'asc' | 'desc' | null
  onSort?: () => void
  align?: 'start' | 'center' | 'end'
}

export function Th({ className, sortable, sortDir, onSort, align = 'start', children, ...props }: ThProps) {
  const alignCls = align === 'center' ? 'text-center' : align === 'end' ? 'text-end' : 'text-start'

  return (
    <th
      scope="col"
      aria-sort={sortDir ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn(
        'whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-fg-muted',
        alignCls,
        className,
      )}
      {...props}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn('inline-flex items-center gap-1.5 transition-colors hover:text-fg', sortDir && 'text-fg')}
        >
          {children}
          {sortDir === 'asc' ? (
            <ArrowUp className="size-3.5" />
          ) : sortDir === 'desc' ? (
            <ArrowDown className="size-3.5" />
          ) : (
            <ChevronsUpDown className="size-3.5 opacity-50" />
          )}
        </button>
      ) : (
        children
      )}
    </th>
  )
}

export function Td({ className, align = 'start', ...props }: Omit<HTMLAttributes<HTMLTableCellElement>, 'align'> & { align?: 'start' | 'center' | 'end' }) {
  const alignCls = align === 'center' ? 'text-center' : align === 'end' ? 'text-end' : 'text-start'
  return <td className={cn('px-4 py-3 text-fg align-middle', alignCls, className)} {...props} />
}

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <Tbody>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c} className="px-4 py-3">
              <span className="block h-4 animate-pulse rounded bg-bg-subtle" style={{ width: `${50 + ((r + c) % 4) * 12}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </Tbody>
  )
}

export function EmptyState({
  icon, title, description, action,
}: { icon?: ReactNode; title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {icon && <div className="text-fg-subtle">{icon}</div>}
      <div>
        <p className="text-sm font-medium text-fg">{title}</p>
        {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
      </div>
      {action}
    </div>
  )
}
